import {
    MeetingTranscriptSources,
    TMeetingTranscriptResponse,
    PriorityListConfig,
    BusinessTrendsConfig
} from '~/apps/main/CompanySettings/AISettings/AISettings.consts';
import { BaseHttpAdapter } from '~/services/adapters/base-http.adapter';
import { IFlowTemplate } from '~/types/model.types';
export class AiPreferencesAdapter extends BaseHttpAdapter {
    // update Hot leads
    updatePriorityLeadOptIn(optIn: boolean) {
        return this.apiService.apiRequest<void>({
            method: 'POST',
            url: `/api/v2/update_hot_leads_opt_in/${optIn}`,
            data: {}
        });
    }

    // update AI for projects + leads
    updateAISuggestionEnabled(optIn: boolean) {
        return this.apiService.apiRequest<void>({
            method: 'POST',
            url: `/api/v2/suggestion/toggle`,
            data: {
                opt_in: optIn
            }
        });
    }

    // update AI injection into composer
    updateAIComposerInjectionEnabled(optIn: boolean) {
        return this.apiService.apiRequest<void>({
            method: 'POST',
            url: `/api/v2/suggestion/toggle_composer_ai_injection`,
            data: {
                opt_in: optIn
            }
        });
    }

    updateProjectRecapsEnabled(optIn: boolean) {
        return this.apiService.apiRequest<void>({
            method: 'PUT',
            url: `/api/v2/project_recaps/pre_meeting_settings`,
            data: {
                enabled: optIn
            }
        });
    }

    updateProjectRecapsForProjectDatesEnabled(optIn: boolean) {
        return this.apiService.apiRequest<void>({
            method: 'PUT',
            url: `/api/v2/project_recaps/pre_project_settings`,
            data: {
                enabled: optIn
            }
        });
    }

    getIsProjectRecapsEnabled() {
        return this.apiService.apiRequest<{
            enabled: boolean;
            is_gate_open: boolean;
        }>({
            method: 'GET',
            url: `/api/v2/project_recaps/pre_meeting_settings`
        });
    }
    getIsProjectRecapsForProjectDatesEnabled() {
        return this.apiService.apiRequest<{
            enabled: boolean;
            is_gate_open: boolean;
        }>({
            method: 'GET',
            url: `/api/v2/project_recaps/pre_project_settings`
        });
    }
    getIsMeetingTranscriptEnabled(source: MeetingTranscriptSources) {
        return this.apiService.apiRequest<TMeetingTranscriptResponse>({
            method: 'GET',
            url: `/api/v2/transcript/settings/${source}`
        });
    }

    getIsLeadsCaptureEnabled() {
        return this.apiService.apiRequest<{
            enabled: boolean;
            is_eligible: boolean;
        }>({
            method: 'GET',
            url: `/api/v2/users/leads_capture_enabled`
        });
    }

    updateMeetingTranscriptEnabled({
        sources,
        enabled
    }: {
        sources: MeetingTranscriptSources[];
        enabled: boolean;
    }) {
        return this.apiService.apiRequest<void>({
            method: 'PUT',
            url: `/api/v2/transcript/settings`,
            data: {
                sources,
                enabled
            }
        });
    }

    updateLeadsCaptureEnabled(optIn: boolean) {
        return this.apiService.apiRequest<void>({
            method: 'PUT',
            url: `/api/v2/users/leads_capture_enabled`,
            data: {
                enabled: optIn
            }
        });
    }

    getIsInPersonAssistantEnabled() {
        return this.apiService.apiRequest<{
            enabled: boolean;
            is_gate_open: boolean;
        }>({
            method: 'GET',
            url: '/api/v2/transcript/audio_settings'
        });
    }

    updateInPersonAssistantEnabled(optIn: boolean) {
        return this.apiService.apiRequest<void>({
            method: 'PUT',
            url: `/api/v2/transcript/audio_settings`,
            data: {
                enabled: optIn
            }
        });
    }

    getPriorityListConfig() {
        return this.apiService.apiRequest<{
            enabled: boolean;
            schedule: string;
        }>({
            method: 'GET',
            url: `/api/v2/users/priority_list_config`
        });
    }

    updatePriorityListConfig(config: Partial<PriorityListConfig>) {
        return this.apiService.apiRequest<{
            enabled: boolean;
            schedule: string;
        }>({
            method: 'PUT',
            url: `/api/v2/users/priority_list_config`,
            data: {
                enabled: config.isEnabled,
                schedule: config.schedule
            }
        });
    }

    getBusinessTrendsConfig() {
        return this.apiService.apiRequest<{
            enabled: boolean;
            schedule: string;
        }>({
            method: 'GET',
            url: `/api/v2/users/business_trends_config`
        });
    }

    updateBusinessTrendsConfig(config: Partial<BusinessTrendsConfig>) {
        return this.apiService.apiRequest<{
            enabled: boolean;
            schedule: string;
        }>({
            method: 'PUT',
            url: `/api/v2/users/business_trends_config`,
            data: {
                enabled: config.isEnabled,
                schedule: config.schedule
            }
        });
    }

    getClientReferralsConfig(): Promise<{
        enabled: boolean;
        is_eligible: boolean;
        client_referrals_lead_form_id?: string; // optional because for the server may have not created the client referrals lead form yet
    }> {
        return Promise.resolve({
            enabled: false,
            is_eligible: false
            // FOR DEBUG:
            // client_referrals_lead_form_id: '68c16ed1ef0f9fafc5c6be89'
        });
        // // TODO: uncomment this after BE is ready
        // return this.apiService.apiRequest<{
        //     enabled: boolean;
        //     is_eligible: boolean;
        //     client_referrals_lead_form_id?: string;
        // }>({
        //     method: 'GET',
        //     url: `/api/v2/referral_request_suggestions/config`
        // });
    }

    updateClientReferralsConfig({
        enabled,
        client_referrals_lead_form_id
    }: {
        enabled: boolean;
        client_referrals_lead_form_id?: string; // needed in case of changing the active client referrals lead form
    }): Promise<{
        client_referrals_lead_form_id?: string; // needed in case of turning on the toggle when there is no client referrals lead form, so the server creates a new one
    }> {
        return Promise.resolve({
            // FOR DEBUG:
            // client_referrals_lead_form_id: '6617d709ef0f9ff7ef2e91df'
        });
        // // TODO: uncomment this after BE is ready
        // return this.apiService.apiRequest<{
        //     client_referrals_lead_form_id?: string;
        // }>({
        //     method: 'PUT',
        //     url: `/api/v2/referral_request_suggestions/config`,
        //     data: {
        //         enabled,
        //         client_referrals_lead_form_id
        //     }
        // });
    }

    updateFollowUpSuggestionsEnabled(optIn: boolean) {
        return this.apiService.apiRequest<void>({
            method: 'PUT',
            url: `/api/v2/suggestions/unconverted_lead_followup_suggestions_config`,
            data: {
                enabled: optIn
            }
        });
    }

    getIsFollowUpSuggestionsEnabled() {
        return this.apiService.apiRequest<{
            enabled: boolean;
            is_eligible: boolean;
        }>({
            method: 'GET',
            url: `/api/v2/suggestions/unconverted_lead_followup_suggestions_config`
        });
    }

    updateSuggestedActionsEnabled(optIn: boolean) {
        return Promise.resolve(); //TODO: Add API call
    }

    getSuggestedArchivedWorkspacesEnabled() {
        return this.apiService.apiRequest<{
            enabled: boolean;
            is_eligible: boolean;
        }>({
            method: 'GET',
            url: `/api/v2/workspaces/suggested_archived_workspaces_config`
        });
    }

    updateSuggestedArchivedWorkspacesEnabled(config: { isEnabled: boolean }) {
        return this.apiService.apiRequest<{
            enabled: boolean;
        }>({
            method: 'PUT',
            url: `/api/v2/workspaces/suggested_archived_workspaces_config`,
            data: {
                enabled: config.isEnabled
            }
        });
    }
}
